import { Fragment, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useInfiniteQuery } from '@tanstack/react-query';
import type { AdminProduct, ProductLine, ProductStatus } from '@popcart/core/v1';
import { api } from '../api';
import { LineDot, Loading, Segmented, StatusPill, arrowNav } from '../ui/bits';
import { Fig, PageHead, Stat } from '../ui/viz';
import { Meter, StDot, dotTone } from './kit';
import { count, peso } from '../lib/format.js';
import { ProductArt, Scene, SceneError } from './art';
import { Icon } from '../ui/Icon';
import {
  LINE_LABEL,
  LOW_GRAMS,
  LOW_PIECES,
  MAX_Q,
  STATUS_LABEL,
  STATUS_TONE,
  canAdjustStock,
  lineTabs,
  listParams,
  priceLabel,
  qtyLabel,
  readFilters,
  stockState,
} from './lib/list.js';
import { listName, type MartAdminProduct } from './lib/mart.js';
import { useRestore, useSetVisible } from './hooks';
import { catalogKeys } from './keys';
import { StockAdjust } from './StockAdjust';
import { PhotoBadge, ReviewBadge } from './ReviewBadge';
import { PHOTO_MISSING, PHOTO_PARAM, filterByPhoto, photoBadge, readPhotoFilter, visibilityBlock } from './lib/photo-rule.js';
import './catalog.css';
import { Select } from '../ui/controls';

export interface ListShop {
  id: string;
  name: string;
  lines: ProductLine[];
}

/**
 * The product list shared by Partner Hub (`scope: 'shop'`, one shop) and Ops (`scope: 'ops'`,
 * every shop with a shop filter). Filters live in the URL (?q=&line=&status=&shop=&photo=missing).
 * Thumbnails are always the brand drawing (owner rule, 0013): the real photo is for the product page.
 */
export function ProductList({
  scope,
  shopId,
  lines,
  shops,
  editPath,
  newPath,
  heading,
  sub,
}: {
  scope: 'shop' | 'ops';
  shopId?: string;
  lines: ProductLine[];
  shops?: ListShop[];
  editPath(p: AdminProduct): string;
  newPath: string | null;
  heading: string;
  sub?: ReactNode;
}) {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const filters = readFilters(params, lines);
  const [q, setQ] = useState(filters.q);
  const [stockOpen, setStockOpen] = useState<string | null>(null);

  // Debounce the search box into the URL.
  useEffect(() => {
    if (q === filters.q) return;
    const t = window.setTimeout(() => setParam('q', q.trim() ? q : null), 250);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  function setParam(k: string, v: string | null) {
    const p = new URLSearchParams(params);
    if (v === null || v === '') p.delete(k);
    else p.set(k, v);
    setParams(p, { replace: true });
  }

  const query = useMemo(() => listParams({ ...filters, shopId: scope === 'ops' ? filters.shopId : '' }), [filters.q, filters.line, filters.status, filters.shopId, scope]); // eslint-disable-line react-hooks/exhaustive-deps
  const list = useInfiniteQuery({
    queryKey: catalogKeys.list(scope === 'shop' ? `shop:${shopId}` : 'ops', query),
    queryFn: ({ pageParam }) =>
      scope === 'shop' ? api.adminProducts(shopId ?? '', { ...query, cursor: pageParam }) : api.opsProducts({ ...query, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: scope === 'ops' || !!shopId,
    refetchInterval: 30_000,
  });
  const photoOnly = readPhotoFilter(params);
  const loaded = useMemo(() => list.data?.pages.flatMap((p) => p.items) ?? [], [list.data]);
  // "Needs a photo" filters the loaded pages (the API list has no photo filter); "Show more" still pages.
  const rows = useMemo(() => filterByPhoto(loaded, photoOnly), [loaded, photoOnly]);
  const visible = useSetVisible();
  const restore = useRestore();

  const tabs = lineTabs(lines);
  const addTo = newPath && filters.line !== 'all' ? `${newPath}${newPath.includes('?') ? '&' : '?'}line=${filters.line}` : newPath;
  const showShop = scope === 'ops' && !filters.shopId;
  const statusWord = STATUS_LABEL[filters.status].toLowerCase();
  const needsPhotoBadge = (p: AdminProduct) => !!photoBadge(p);

  const counts = useMemo(() => {
    let low = 0;
    let out = 0;
    let photo = 0;
    for (const p of loaded) {
      if (!p.isCombo) {
        const s = stockState(p);
        if (s === 'low') low++;
        else if (s === 'sold_out') out++;
      }
      if (needsPhotoBadge(p)) photo++;
    }
    return { low, out, photo };
  }, [loaded]);
  const more = list.hasNextPage ? '+' : '';

  return (
    <>
      <PageHead
        title={heading}
        meta={sub}
        actions={
          <>
            <label className="search">
              <span className="sr-only">Search products</span>
              <Icon name="search" />
              <input type="search" placeholder="Find a product" value={q} maxLength={MAX_Q} onChange={(e) => setQ(e.target.value)} />
            </label>
            {addTo ? (
              <Link className="btn btn-primary" to={addTo}>
                <Icon name="plus" />
                Add product
              </Link>
            ) : null}
          </>
        }
      />

      {list.data ? (
        <div className="dv-stats mk-pstats dv-rise" style={{ ['--i' as string]: 0 }}>
          <Stat label={`${STATUS_LABEL[filters.status]} products`} value={`${count(loaded.length)}${more}`} sub={showShop ? 'All shops' : undefined} />
          <Stat label="Low stock" value={count(counts.low)} sub={counts.low ? 'Restock soon' : 'All good'} />
          <Stat label="Sold out" value={count(counts.out)} tone={counts.out ? 'alert' : undefined} sub={counts.out ? 'Customers can’t order' : 'None'} />
          <Stat label="Need a photo" value={count(counts.photo)} sub={photoOnly ? 'Filtered' : undefined} onOpen={counts.photo || photoOnly ? () => setParam(PHOTO_PARAM, photoOnly ? null : PHOTO_MISSING) : undefined} />
        </div>
      ) : null}

      <div className="row wrap cat-filters">
        {scope === 'ops' && shops ? (
          <div className="field cat-shopfilter">
            <label htmlFor="cat-shop" className="sr-only">
              Shop
            </label>
            <Select id="cat-shop" className="input" value={filters.shopId} onChange={(e) => setParam('shop', e.target.value || null)}>
              <option value="">All shops</option>
              {shops.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </Select>
          </div>
        ) : null}
        {tabs.length > 2 ? (
          <div className="tabs" role="tablist" aria-label="Product line" onKeyDown={(e) => arrowNav(e, '[role="tab"]')}>
            {tabs.map((t) => (
              <button
                key={t.value}
                type="button"
                role="tab"
                aria-selected={filters.line === t.value}
                tabIndex={filters.line === t.value ? 0 : -1}
                className="pill tab chip-outline"
                onClick={() => setParam('line', t.value === 'all' ? null : t.value)}
              >
                {t.label}
              </button>
            ))}
          </div>
        ) : null}
        <button
          type="button"
          className="pill tab chip-outline cat-photofilter"
          aria-pressed={photoOnly}
          onClick={() => setParam(PHOTO_PARAM, photoOnly ? null : PHOTO_MISSING)}
        >
          Needs a photo
        </button>
        <span className="grow" />
        <Segmented<ProductStatus>
          label="Status"
          value={filters.status}
          options={[
            { value: 'active', label: 'Active' },
            { value: 'hidden', label: 'Hidden' },
            { value: 'archived', label: 'Archived' },
          ]}
          onChange={(v) => setParam('status', v === 'active' ? null : v)}
        />
      </div>

      <section className="card dv-card dv-rise" aria-label="Products" aria-busy={list.isFetching || undefined} style={{ ['--i' as string]: 1 }}>
        {list.isError ? (
          <SceneError error={list.error} onRetry={() => void list.refetch()} />
        ) : !list.data ? (
          <Loading label="Loading products" rows={4} />
        ) : rows.length === 0 && photoOnly ? (
          <Scene
            scene="noResults"
            title={loaded.length ? 'Every product here has a photo' : `No ${statusWord} products`}
            action={
              <button type="button" className="btn" onClick={() => setParam(PHOTO_PARAM, null)}>
                Show all products
              </button>
            }
          >
            {list.hasNextPage ? 'This checks the products loaded so far. Show all, then load more to check the rest.' : 'Customers see a real photo on every product page.'}
          </Scene>
        ) : rows.length === 0 ? (
          <Scene
            scene={filters.q || filters.line !== 'all' ? 'noResults' : 'noProducts'}
            title={filters.q || filters.line !== 'all' ? 'No products match' : filters.status === 'active' ? 'The shelf is empty' : `No ${statusWord} products`}
            action={
              addTo && filters.status === 'active' && !filters.q ? (
                <Link className="btn" to={addTo}>
                  Add the first one
                </Link>
              ) : undefined
            }
          >
            {filters.status === 'archived'
              ? 'Archived products had orders before they were removed. Restore one to sell it again.'
              : filters.status === 'hidden'
                ? 'Hidden products stay here, invisible to customers, until you show them.'
                : filters.q || filters.line !== 'all'
                  ? 'Try another search, line or status.'
                  : 'Products you add show up here and in the customer app.'}
          </Scene>
        ) : (
          <div className="table-wrap">
            <table className="cat-table mk-table">
              <thead>
                <tr>
                  <th scope="col">
                    <span className="sr-only">Photo</span>
                  </th>
                  <th scope="col">Product</th>
                  <th scope="col" className="num">
                    Price
                  </th>
                  <th scope="col">Category</th>
                  <th scope="col" className="num">
                    Stock
                  </th>
                  <th scope="col">Status</th>
                  <th scope="col">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => {
                  const state = stockState(p);
                  const open = stockOpen === p.id;
                  const to = editPath(p);
                  const price = priceParts(p);
                  const variants = (p as MartAdminProduct).variantCount ?? 0;
                  return (
                    <Fragment key={p.id}>
                      <tr
                        className={`cat-row${p.status === 'active' ? '' : ' cat-off'}`}
                        onClick={(e) => {
                          if ((e.target as HTMLElement).closest('a,button,input,select,label')) return;
                          navigate(to);
                        }}
                      >
                        <td className="cat-photo-cell">
                          <ProductArt className="cat-thumb" name={p.name} line={p.line} category={p.categoryName} />
                        </td>
                        <td>
                          <div className="stack s4">
                            <Link className="cat-name" to={to}>
                              {listName(p)}
                            </Link>
                            <span className="mk-st cat-meta">
                              <LineDot line={p.line} />
                              <span>{LINE_LABEL[p.line]}</span>
                              {p.isCombo ? <span className="mk-tag">{p.line === 'mart' ? 'Value pack' : 'Combo'}</span> : null}
                              {variants ? <span className="mk-sub">· {variantsText(variants)}</span> : null}
                              {p.hasModifiers ? <span className="mk-sub">· Has choices</span> : null}
                              {showShop ? <span className="mk-sub">· {p.shopName}</span> : null}
                            </span>
                          </div>
                        </td>
                        <td className="num">
                          <span className="mk-price" title={priceLabel(p)}>
                            <Fig text={price.amount} size="sm" animate={false} />
                            {price.unit ? <span className="u">/ {price.unit}</span> : null}
                          </span>
                        </td>
                        <td>{p.categoryName ? <span className="mk-sub mk-cat">{p.categoryName}</span> : <span className="muted">—</span>}</td>
                        <td className="num">
                          {p.isCombo ? (
                            <span className="mk-sub" title="Stock comes from its items">
                              From items
                            </span>
                          ) : (
                            <span className={`mk-stock ${state}`} title={p.onHand !== p.available ? `${qtyLabel(p.onHand, p.stockUnit)} on hand` : undefined}>
                              <span className="n">
                                {state === 'sold_out' ? <StatusPill tone="error">Sold out</StatusPill> : state === 'low' ? <span className="mk-low">Low</span> : null}
                                <span className="mk-numtxt">{qtyLabel(p.available, p.stockUnit)}</span>
                              </span>
                              <Meter value={stockFill(p)} tone={state === 'sold_out' ? 'bad' : state === 'low' ? 'warn' : 'ok'} />
                            </span>
                          )}
                        </td>
                        <td>
                          <span className="stack s4 cat-status">
                            <StDot tone={dotTone(STATUS_TONE[p.status])}>{STATUS_LABEL[p.status]}</StDot>
                            <ReviewBadge product={p} />
                            <PhotoBadge product={p} />
                          </span>
                        </td>
                        <td>
                          <div className="actions">
                            {p.status === 'archived' ? (
                              <button
                                type="button"
                                className="btn"
                                disabled={restore.isPending && restore.variables?.id === p.id}
                                onClick={() => restore.mutate(p)}
                                aria-label={`Restore ${p.name}`}
                              >
                                Restore
                              </button>
                            ) : (
                              <>
                                <button
                                  type="button"
                                  role="switch"
                                  className="switch"
                                  aria-checked={p.status === 'active'}
                                  aria-label={`${p.name} on sale`}
                                  title={visibilityBlock(p) ?? (p.status === 'active' ? 'On sale' : 'Hidden')}
                                  disabled={(visible.isPending && visible.variables?.product.id === p.id) || !!visibilityBlock(p)}
                                  onClick={() => visible.mutate({ product: p, visible: p.status !== 'active' })}
                                >
                                  <span className="track" aria-hidden="true" />
                                  <span className="sr-only cat-switch-label">{p.status === 'active' ? 'On sale' : 'Hidden'}</span>
                                </button>
                                {canAdjustStock(p) ? (
                                  <button
                                    type="button"
                                    className={open ? 'dv-kebab press on' : 'dv-kebab press'}
                                    aria-expanded={open}
                                    aria-controls={`stk-row-${p.id}`}
                                    aria-label={`Adjust stock for ${p.name}`}
                                    title="Adjust stock"
                                    onClick={() => setStockOpen(open ? null : p.id)}
                                  >
                                    <span aria-hidden="true">±</span>
                                  </button>
                                ) : null}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                      {open ? (
                        <tr id={`stk-row-${p.id}`} className="cat-stockrow">
                          <td />
                          <td colSpan={6}>
                            <StockAdjust product={p} compact onDone={() => setStockOpen(null)} />
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {list.hasNextPage ? (
          <div className="row cat-more">
            <button type="button" className="btn" onClick={() => void list.fetchNextPage()} disabled={list.isFetchingNextPage} aria-busy={list.isFetchingNextPage || undefined}>
              {list.isFetchingNextPage ? 'Loading…' : 'Show more products'}
            </button>
          </div>
        ) : null}
      </section>
    </>
  );
}

/** "₱170" + "kg" / "₱45" + "250 g pack": the amount as a figure, the unit quiet beside it. */
function priceParts(p: AdminProduct): { amount: string; unit: string | null } {
  if (p.pricing === 'weight') return { amount: peso(p.pricePerKgCentavos ?? 0), unit: 'kg' };
  return { amount: peso(p.priceCentavos ?? 0), unit: p.unitLabel ? p.unitLabel.replace(/^\s*\/\s*/, '') || null : null };
}

/** How full the shelf looks: four times the low line reads as full. */
function stockFill(p: AdminProduct) {
  const full = (p.stockUnit === 'gram' ? LOW_GRAMS : LOW_PIECES) * 4;
  return Math.max(0, Math.min(1, p.available / full));
}

/** "2 variants" under a Grocery base product. */
function variantsText(n: number) {
  return `${n} ${n === 1 ? 'variant' : 'variants'}`;
}
